/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { compareNumbers, compareStrings, normalizeString, ZONEOFFSET_HASH_SEED, ZONE_HASH_SEED } from './internal/core';
import { D2 } from "./internal/formats";
import { ZonedDateTime } from './ZonedDateTime';
import { Instant } from './Instant';
import { Equatable, Comparable, Equaler } from '@esfx/equatable';

const offsetStringRegExp = /^(?<sign>[+-])(?<hours>\d{2})(?:(?<colon>:)?(?<minutes>\d{2})(?:\k<colon>(?<seconds>\d{2}))?)?$/;

export abstract class Zone implements Equatable, Comparable {
    static get UTC() { return UTC; }
    static get SYSTEM() { return SYSTEM; }

    abstract get ianaZone(): string;
    abstract get timeZone(): string;

    abstract getOffset(instant: Instant): ZoneOffset;

    getOffsetSeconds(instant: Instant): number {
        return this.getOffset(instant).offsetSeconds;
    }

    getOffsetString(instant: Instant) {
        return this.getOffset(instant).offsetString;
    }

    getZoneString(instant: Instant) {
        return `${this.getOffsetString(instant)}${this.ianaZone ? `[${this.ianaZone}]` : ""}`;
    }

    withInstant(instant: Instant) {
        return new ZonedDateTime(instant, this);
    }

    abstract equals(other: Zone): boolean;

    compareTo(other: Zone): number {
        return compareNumbers(this.getOffsetSeconds(Instant.EPOCH), other.getOffsetSeconds(Instant.EPOCH))
            || compareStrings(this.timeZone, other.timeZone);
    }

    toString() {
        return this.timeZone;
    }

    toJSON() {
        return this.timeZone;
    }

    static fromOffset(offsetSeconds: number): ZoneOffset {
        return new ZoneOffset(offsetSeconds);
    }

    static fromString(zone: string): Zone {
        if (zone === "Z" || zone === "UTC") return ZoneOffset.UTC;
        if (zone === "SYSTEM") return Zone.SYSTEM;
        const match = offsetStringRegExp.exec(normalizeString(zone));
        if (match) {
            const { sign, hours, minutes = "", seconds = "" } = match.groups!;
            const offsetSeconds = +hours * 3600 + +minutes * 60 + +seconds;
            return new ZoneOffset(sign === "-" ? -offsetSeconds : offsetSeconds);
        }
        return new IanaZone(zone);
    }

    [Equatable.equals](other: unknown) {
        return other instanceof Zone
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = ZONE_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.ianaZone);
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this.timeZone);
        return hc;
    }

    [Comparable.compareTo](other: unknown) {
        if (!(other instanceof Zone)) throw new TypeError();
        return this.compareTo(other);
    }
}

Object.defineProperty(Zone.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "Zone"
});

export class ZoneOffset extends Zone implements Equatable, Comparable {
    static readonly MIN = new ZoneOffset(-64800);
    static readonly MAX = new ZoneOffset(64800);

    private _offsetSeconds: number;

    constructor(offsetSeconds: number) {
        super();
        this._offsetSeconds = offsetSeconds;
    }

    get ianaZone() { return ""; }
    get timeZone() { return this.offsetString; }
    get offsetSeconds() { return this._offsetSeconds; }
    get offsetString() {
        let offsetSeconds = this._offsetSeconds;
        if (offsetSeconds === 0) return "Z";
        let sign = "+";
        if (offsetSeconds < 0) {
            sign = "-";
            offsetSeconds = -offsetSeconds;
        }

        const h = Math.floor(offsetSeconds / 3600);
        const m = Math.floor(offsetSeconds / 60) % 60;
        const s = offsetSeconds % 60;
        return `${sign}${D2(h)}:${D2(m)}${s ? `:${D2(s)}` : ""}`;
    }

    getOffset(instant: Instant): ZoneOffset;
    getOffset(_instant: Instant) {
        return this;
    }

    getOffsetSeconds(instant: Instant): number;
    getOffsetSeconds(_instant: Instant) {
        return this.offsetSeconds;
    }

    getOffsetString(instant: Instant): string;
    getOffsetString(_instant: Instant) {
        return this.offsetString;
    }

    equals(other: Zone): boolean {
        return other instanceof ZoneOffset
            && this._offsetSeconds === other._offsetSeconds;
    }

    static fromString(text: string): ZoneOffset {
        if (text === "Z" || text === "UTC") return ZoneOffset.UTC;
        const match = offsetStringRegExp.exec(normalizeString(text));
        if (match) {
            const { sign, hours, minutes = "", seconds = "" } = match.groups!;
            const offsetSeconds = +hours * 3600 + +minutes * 60 + +seconds;
            return new ZoneOffset(sign === "-" ? -offsetSeconds : offsetSeconds);
        }
        throw new SyntaxError(`Invalid offset string '${text}'.`);
    }

    [Equatable.equals](other: unknown): boolean {
        return other instanceof ZoneOffset
            && this.equals(other);
    }

    [Equatable.hash]() {
        let hc = ZONEOFFSET_HASH_SEED;
        hc = ((hc << 7) | (hc >>> 25)) ^ Equaler.defaultEqualer.hash(this._offsetSeconds);
        return hc;
    }

    [Comparable.compareTo](other: unknown): number {
        if (!(other instanceof ZoneOffset)) throw new TypeError();
        return this.compareTo(other);
    }
}

Object.defineProperty(ZoneOffset.prototype, Symbol.toStringTag, {
    writable: true,
    configurable: true,
    value: "ZoneOffset"
});

const UTC = new ZoneOffset(0);
const SYSTEM: Zone = new class extends Zone {
    get ianaZone() { return ""; }
    get timeZone() { return "SYSTEM"; }
    equals(other: Zone): boolean { return other === SYSTEM; }
    getOffset(instant: Instant) { return new ZoneOffset(new Date(instant.milliseconds).getTimezoneOffset() * -60); }
};

class IanaZone extends Zone {
    private _ianaZone: string;

    constructor(ianaZone: string) {
        super();
        this._ianaZone = ianaZone;
    }

    get ianaZone() { return this._ianaZone; }
    get timeZone() { return this._ianaZone; }

    equals(other: Zone): boolean {
        return other instanceof IanaZone
            && this._ianaZone === other._ianaZone;
    }

    getOffset(instant: Instant) {
        const format = new Intl.DateTimeFormat("en-iso", {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            second: "numeric",
            hour12: false,
            timeZone: this._ianaZone
        });

        let year = 0, month = 0, day = 0, hour = 0, minute = 0, second = 0;
        for (const part of format.formatToParts(new Date(instant.milliseconds))) {
            switch (part.type) {
                case "year": year = +part.value; break;
                case "month": month = +part.value; break;
                case "day": day = +part.value; break;
                case "hour": hour = +part.value; break;
                case "minute": minute = +part.value; break;
                case "second": second = +part.value; break;
            }
        }

        const utcTime = Date.UTC(year, month - 1, day, hour, minute, second, instant.milliseconds % 1e3);
        const offsetSeconds = Math.floor((utcTime - instant.milliseconds) / 1e3);
        return new ZoneOffset(offsetSeconds);
    }
}